import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { ChittyModule, SystemScanResult, PersonaConfig } from '../types';
import { TauriAPI } from '../lib/tauri';
import { AgentEcosystem } from './AgentEcosystem';
import {
  Monitor,
  Package,
  Activity,
  Settings,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Users,
  Link,
  Bot,
  Zap
} from 'lucide-react';

interface DashboardProps {
  systemScanResult?: SystemScanResult;
  selectedPersona?: PersonaConfig;
  onReconfigure: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({
  systemScanResult,
  selectedPersona,
  onReconfigure,
}) => {
  const [modules, setModules] = useState<ChittyModule[]>([]);
  const [systemHealth, setSystemHealth] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      const [availableModules, health] = await Promise.all([
        TauriAPI.getAvailableModules(),
        TauriAPI.getSystemHealth(),
      ]);
      
      setModules(availableModules);
      setSystemHealth(health);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getModuleStats = () => {
    const total = modules.length;
    const installed = modules.filter(m => m.installed).length;
    const enabled = modules.filter(m => m.enabled).length;
    const needsConfig = modules.filter(m => m.config_required).length;
    
    return { total, installed, enabled, needsConfig };
  };

  const getSystemHealthScore = () => {
    if (!systemHealth || Object.keys(systemHealth).length === 0) return 0;
    
    // Simple health score calculation
    const services = Object.values(systemHealth.services || {});
    if (services.length === 0) return 0;
    
    const healthyServices = services.filter(service => 
      typeof service === 'object' && service !== null && (service as any).status === 'healthy'
    ).length;
    
    return Math.round((healthyServices / services.length) * 100);
  };

  const moduleStats = getModuleStats();
  const healthScore = getSystemHealthScore();

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">ChittyOS Dashboard</h1>
          <p className="text-muted-foreground">
            {selectedPersona 
              ? `Configured for ${selectedPersona.name} workflow`
              : 'Monitor and manage your ChittyOS ecosystem'
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadDashboardData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={onReconfigure}>
            <Settings className="w-4 h-4 mr-2" />
            Reconfigure
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Health</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{healthScore}%</div>
            <div className="flex items-center gap-2 mt-2">
              <Progress value={healthScore} className="h-2 flex-1" />
              {healthScore >= 80 ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <AlertCircle className="w-4 h-4 text-yellow-500" />
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Modules</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{moduleStats.enabled}/{moduleStats.installed}</div>
            <p className="text-xs text-muted-foreground">
              {moduleStats.enabled} enabled of {moduleStats.installed} installed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Integrations</CardTitle>
            <Link className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {systemScanResult?.potential_integrations.length || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Available integrations
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Performance</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {systemScanResult ? Math.round(systemScanResult.system_info.available_memory / systemScanResult.system_info.total_memory * 100) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">
              Memory available
            </p>
          </CardContent>
        </Card>
      </div>

      {/* System Information */}
      {systemScanResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Monitor className="w-5 h-5" />
              System Information
            </CardTitle>
            <CardDescription>Current system configuration</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-sm font-medium">OS</p>
                <p className="text-sm text-muted-foreground">
                  {systemScanResult.system_info.os_name} {systemScanResult.system_info.os_version}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium">Architecture</p>
                <p className="text-sm text-muted-foreground">
                  {systemScanResult.system_info.arch}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium">CPU Cores</p>
                <p className="text-sm text-muted-foreground">
                  {systemScanResult.system_info.cpu_count}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium">Memory</p>
                <p className="text-sm text-muted-foreground">
                  {Math.round(systemScanResult.system_info.total_memory / 1024 / 1024 / 1024)} GB
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Module Status */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Module Status
            </CardTitle>
            <CardDescription>Current state of your installed modules</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {modules.filter(m => m.installed).slice(0, 6).map((module) => (
                <div key={module.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Package className="w-4 h-4" />
                    <div>
                      <p className="font-medium">{module.name}</p>
                      <p className="text-sm text-muted-foreground">v{module.version}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {module.enabled ? (
                      <Badge variant="success">Running</Badge>
                    ) : (
                      <Badge variant="secondary">Stopped</Badge>
                    )}
                    {module.config_required && (
                      <Badge variant="warning">Config</Badge>
                    )}
                  </div>
                </div>
              ))}
              {modules.filter(m => m.installed).length > 6 && (
                <p className="text-sm text-muted-foreground text-center pt-2">
                  +{modules.filter(m => m.installed).length - 6} more modules
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Service Health */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Service Health
            </CardTitle>
            <CardDescription>Status of ChittyOS services</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {systemHealth.services ? (
                Object.entries(systemHealth.services).map(([serviceName, status]) => (
                  <div key={serviceName} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Zap className="w-4 h-4" />
                      <div>
                        <p className="font-medium">{serviceName}</p>
                        <p className="text-sm text-muted-foreground">
                          {typeof status === 'object' && status !== null 
                            ? (status as any).uptime || 'Unknown'
                            : 'N/A'
                          }
                        </p>
                      </div>
                    </div>
                    <Badge 
                      variant={
                        typeof status === 'object' && status !== null && (status as any).status === 'healthy' 
                          ? 'success' 
                          : 'destructive'
                      }
                    >
                      {typeof status === 'object' && status !== null 
                        ? (status as any).status || 'Unknown'
                        : 'Offline'
                      }
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No service health data available
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Persona Information */}
      {selectedPersona && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Active Persona: {selectedPersona.name}
            </CardTitle>
            <CardDescription>{selectedPersona.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-3">Primary Services</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedPersona.primary_services.map((service) => (
                    <Badge key={service} variant="secondary">
                      {service}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-3">Recommended Modules</h4>
                <div className="space-y-2">
                  {selectedPersona.recommended_modules.map((moduleId) => {
                    const module = modules.find(m => m.id === moduleId);
                    return (
                      <div key={moduleId} className="flex items-center justify-between">
                        <span className="text-sm">{module?.name || moduleId}</span>
                        {module && (
                          <Badge 
                            variant={module.enabled ? 'success' : 'outline'}
                            className="text-xs"
                          >
                            {module.enabled ? 'Active' : 'Inactive'}
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Agent Ecosystem */}
      <AgentEcosystem 
        selectedPersona={selectedPersona?.id}
        onAgentInvoke={(agentName, result) => {
          console.log(`Agent ${agentName} invoked:`, result);
          // Could show toast notification or update UI
        }}
      />

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks and shortcuts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button onClick={onReconfigure} variant="outline" className="h-auto flex-col gap-2 p-4">
              <RefreshCw className="w-6 h-6" />
              <span>Reconfigure</span>
            </Button>
            <Button variant="outline" className="h-auto flex-col gap-2 p-4">
              <Settings className="w-6 h-6" />
              <span>System Settings</span>
            </Button>
            <Button variant="outline" className="h-auto flex-col gap-2 p-4">
              <Bot className="w-6 h-6" />
              <span>Claude Config</span>
            </Button>
            <Button variant="outline" className="h-auto flex-col gap-2 p-4">
              <Activity className="w-6 h-6" />
              <span>View Logs</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};